import { SoSelectObjectItem } from "@/types/components/SoSelect"
import { FilterType, SoDataTableFilters } from "@/types/components/SoDataTable"
import {
  filterTradesDatesColumns,
  filterTradesNumberColumns,
  filterTradesStringColumns,
} from "@/helpers/tradeHelper"

export const tradeProducts = {
  "UK Baseload": {
    hours: 24,
    workingDaysOnly: false,
    allowsPercentOfBaseload: false,
  },
  "UK B1+2": {
    hours: 8,
    workingDaysOnly: false,
    allowsPercentOfBaseload: true,
  },
  "Extended Peaks": {
    hours: 16,
    workingDaysOnly: false,
    allowsPercentOfBaseload: true,
  },
  "UK Block-5": {
    hours: 4,
    workingDaysOnly: true,
    allowsPercentOfBaseload: true,
  },
  "UK Block-6": {
    hours: 4,
    workingDaysOnly: true,
    allowsPercentOfBaseload: true,
  },
  "7 Day Block-5": {
    hours: 4,
    workingDaysOnly: false,
    allowsPercentOfBaseload: true,
  },
  "7 Day Block-6": {
    hours: 4,
    workingDaysOnly: false,
    allowsPercentOfBaseload: true,
  },
  "UK Peakload": {
    hours: 12,
    workingDaysOnly: true,
    allowsPercentOfBaseload: true,
  },
} as const
export type tradeProductOptions = keyof typeof tradeProducts

export const tradeCounterpartyFirstFieldOptions = [
  "Axpo",
  "So Energy",
  "PPA",
  "SEG",
  "ESB",
]
export const tradeCounterpartySecondFieldOptions = ["SVT", "Fixed", "Balancing"]

export const aheadIndexes = [
  "Mid DA Heren - 0.125",
  "Mid DA Heren + 0.125",
  "Mid WEND Heren - 0.125",
  "Mid WEND Heren + 0.125",
]

export const tradeIndexes = ["Fixed", ...aheadIndexes]

export const tradeCustomPeriodValues = [
  "WD",
  "WEND",
  "DA",
  "WDNW",
  "BOW",
  "BOM",
] as const
export type TradeCustomPeriod = (typeof tradeCustomPeriodValues)[number]

export const tradeMonthPeriods = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

export const tradeQuarterAndSeasonalPeriods = {
  Win: {
    start: "Oct",
    end: "Mar",
    getEndYearForPeriod: (year: string) => {
      return `${parseInt(year) + 1}`
    },
  },
  Sum: {
    start: "Apr",
    end: "Sep",
    getEndYearForPeriod: (year: string) => {
      return year
    },
  },
  Q1: {
    start: "Jan",
    end: "Mar",
    getEndYearForPeriod: (year: string) => {
      return year
    },
  },
  Q2: {
    start: "Apr",
    end: "Jun",
    getEndYearForPeriod: (year: string) => {
      return year
    },
  },
  Q3: {
    start: "Jul",
    end: "Sep",
    getEndYearForPeriod: (year: string) => {
      return year
    },
  },
  Q4: {
    start: "Oct",
    end: "Dec",
    getEndYearForPeriod: (year: string) => {
      return year
    },
  },
} as const

export const tradePeriodFirstFieldOptionsElec = tradeMonthPeriods.concat(
  Object.keys(tradeQuarterAndSeasonalPeriods),
)
export const tradePeriodFirstFieldOptionsGas =
  tradePeriodFirstFieldOptionsElec.concat(tradeCustomPeriodValues)

export const tradePeriodSecondFieldOptions: SoSelectObjectItem[] = [
  ...Array(6),
].map((a, b) => {
  const computedYear = (new Date().getFullYear() + b).toString()
  return {
    title: computedYear.substring(2),
    value: computedYear,
  }
})

export const TradeBuySellValues = {
  Buy: { multiplier: 1 },
  Sell: { multiplier: -1 },
} as const

export type TradeBuySell = keyof typeof TradeBuySellValues

export const dateColumns = [
  "date",
  "dateFrom",
  "dateTo",
  "invoiceDateFrom",
  "invoiceDateTo",
  "confirmReceivedDate",
  "confirmSignedDate",
]

export type ElecNominationEntry = {
  Date: Date
  Name: string
  Volume: number
  SP: number
}

export type RawElecNominationData = {
  Date: string
  Name: string
  Volume: string
  SP: string
}

export const elecTradeTableFilters: SoDataTableFilters = {
  date: {
    type: FilterType.DATE_RANGE,
    label: "Date",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "date")
    },
  },
  product: {
    type: FilterType.CHECKBOX,
    label: "Product",
    value: [],
    options: Object.keys(tradeProducts).reduce((acc, current) => {
      acc[current] = current
      return acc
    }, {}),
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "product")
    },
  },
  period: {
    type: FilterType.CHECKBOX,
    label: "Period",
    value: [],
    options: {},
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "period")
    },
  },
  price: {
    type: FilterType.NUMBER_RANGE,
    label: "Price",
    value: {
      min: null,
      max: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesNumberColumns(filters, item, "price")
    },
  },
  volume: {
    type: FilterType.NUMBER_RANGE,
    label: "Volume",
    value: {
      min: null,
      max: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesNumberColumns(filters, item, "volume")
    },
  },
  totalVolume: {
    type: FilterType.NUMBER_RANGE,
    label: "Total Volume",
    value: {
      min: null,
      max: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesNumberColumns(filters, item, "totalVolume")
    },
  },
  percentOfBaseload: {
    type: FilterType.NUMBER_RANGE,
    label: "% of Baseload",
    value: {
      min: null,
      max: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesNumberColumns(filters, item, "percentOfBaseload")
    },
  },
  invoiceDateFrom: {
    type: FilterType.DATE_RANGE,
    label: "Invoice Date From",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "invoiceDateFrom")
    },
  },
  invoiceDateTo: {
    type: FilterType.DATE_RANGE,
    label: "Invoice Date To",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "invoiceDateTo")
    },
  },
  counterParty: {
    type: FilterType.CHECKBOX,
    label: "Counterparty",
    value: [],
    options: {},
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "counterParty")
    },
  },
  trader: {
    type: FilterType.CHECKBOX,
    label: "Trader",
    value: [],
    options: {},
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "trader")
    },
  },
  confirmReceivedDate: {
    type: FilterType.DATE_RANGE,
    label: "Confirm Received Date",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "confirmReceivedDate")
    },
  },
  confirmSignedDate: {
    type: FilterType.DATE_RANGE,
    label: "Confirm Signed Date",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "confirmSignedDate")
    },
  },
  confirmSignedBy: {
    type: FilterType.CHECKBOX,
    label: "ConfirmSignedBy",
    value: [],
    options: {},
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "confirmSignedBy")
    },
  },
}

export const gasTradeTableFilters: SoDataTableFilters = {
  date: {
    type: FilterType.DATE_RANGE,
    label: "Date",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "date")
    },
  },
  period: {
    type: FilterType.CHECKBOX,
    label: "Period",
    value: [],
    options: {},
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "period")
    },
  },
  index: {
    type: FilterType.CHECKBOX,
    label: "Index",
    value: [],
    options: tradeIndexes.reduce((acc, current) => {
      acc[current] = current
      return acc
    }, {}),
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "index")
    },
  },
  price: {
    type: FilterType.NUMBER_RANGE,
    label: "Price",
    value: {
      min: null,
      max: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesNumberColumns(filters, item, "price")
    },
  },
  volume: {
    type: FilterType.NUMBER_RANGE,
    label: "Volume",
    value: {
      min: null,
      max: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesNumberColumns(filters, item, "volume")
    },
  },
  totalVolume: {
    type: FilterType.NUMBER_RANGE,
    label: "Total Volume",
    value: {
      min: null,
      max: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesNumberColumns(filters, item, "totalVolume")
    },
  },
  dateFrom: {
    type: FilterType.DATE_RANGE,
    label: "Date From",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "dateFrom")
    },
  },
  dateTo: {
    type: FilterType.DATE_RANGE,
    label: "Date To",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "dateTo")
    },
  },
  counterParty: {
    type: FilterType.CHECKBOX,
    label: "Counterparty",
    value: [],
    options: {},
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "counterParty")
    },
  },
  trader: {
    type: FilterType.CHECKBOX,
    label: "Trader",
    value: [],
    options: {},
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "trader")
    },
  },
  confirmReceivedDate: {
    type: FilterType.DATE_RANGE,
    label: "Confirm Received Date",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "confirmReceivedDate")
    },
  },
  confirmSignedDate: {
    type: FilterType.DATE_RANGE,
    label: "Confirm Signed Date",
    value: {
      from: null,
      to: null,
    },
    expandable: true,
    filter: (filters, item) => {
      return filterTradesDatesColumns(filters, item, "confirmSignedDate")
    },
  },
  confirmSignedBy: {
    type: FilterType.CHECKBOX,
    label: "ConfirmSignedBy",
    value: [],
    options: {},
    expandable: true,
    filter: (filters, item) => {
      return filterTradesStringColumns(filters, item, "confirmSignedBy")
    },
  },
}
